// src/api/reportClientMixin.js
import client from "@/api/client.js";
import store from "@/store";

// --- helpers ---
function filenameFromDisposition(response, fallback) {
    try {
        const cd =
            response?.headers?.["content-disposition"] ||
            response?.headers?.["Content-Disposition"];
        if (!cd) return fallback;
        // RFC 5987 / basic filename
        const star = /filename\*\s*=\s*(?:UTF-8''|)([^;]+)/i.exec(cd);
        if (star && star[1]) return decodeURIComponent(star[1].replace(/^"|"$|'/g, ""));
        const basic = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
        if (basic && basic[1]) return basic[1];
    } catch (_) {
        console.error("Error parsing filename from Content-Disposition", _);
    }
    return fallback;
}

export default {
    methods: {
        // GET /api2/Report/data/{jobId}?username
        async getReportData(jobId, { username = null } = {}) {
            const meta = { clientApiMethod: "getReportData", input: { jobId, username } };
            const res = await client().get(
                `${store.state.apiRouteRoot}Report/data/${jobId}`,
                { params: { username }, meta }
            );
            return res.data; // ServiceReportDataDto
        },

        // GET /api2/Report/signature/{jobId}?authKey=...
        async getReportSignature(jobId, { authKey = "authentication_key", filename } = {}) {
            const meta = { clientApiMethod: "getReportSignature", input: { jobId } };
            const res = await client().get(
                `${store.state.apiRouteRoot}Report/signature/${jobId}`,
                { params: { authKey }, responseType: "blob", meta }
            );
            const fname = filenameFromDisposition(res, filename || "signature.jpg");
            return { blob: res.data, filename: fname };
        },

        // GET /api2/Report/download/{jobId} -> PDF (blob)
        async downloadReport(jobId, filename) {
            const meta = { clientApiMethod: "downloadReport", input: { jobId } };
            const res = await client().get(
                `${store.state.apiRouteRoot}Report/download/${jobId}`,
                { responseType: "blob", meta }
            );
            const fname = filename || filenameFromDisposition(res, "service_report.pdf");
            return { blob: res.data, filename: fname };
        },

        // POST /api2/Report/generate/{jobId}
        // Generates the current report, saves it as a Job Document.
        async generateAndSaveReport(jobId) {
            const meta = { clientApiMethod: "generateAndSaveReport", input: { jobId } };
            const res = await client().post(`${store.state.apiRouteRoot}Report/generate/${jobId}`, null, { meta });
            return res?.data;
        },

        // POST /api2/Report/sign/{jobId}-> PDF (blob)
        async signReport(jobId, file, filename) {
            const meta = { clientApiMethod: "signReport", input: { jobId } };
            const form = new FormData();
            form.append("file", file);
            const res = await client().post(
                `${store.state.apiRouteRoot}Report/sign/${jobId}`,
                form,
                { responseType: "blob", meta }
            );
            const fname = filenameFromDisposition(res, filename || "service_report_signed.pdf");
            return { blob: res.data, filename: fname };
        },

        // POST /api2/Report/Send/{jobId}
        async sendReportToCustomerContact(jobId) {
            const meta = {
                clientApiMethod: "sendReportToCustomerContact",
                input: { jobId },
                successText: "Report sent to customer",
            };
            const res = await client().post(
                `${store.state.apiRouteRoot}Report/Send/${jobId}`,
                null,
                { meta }
            );
            return res.data; // OK (void)
        },

        // GET /api2/ServiceProvider/CheckListReport/Download/{jobId}
        async downloadCheckListReport(jobId, filename) {
            const meta = { clientApiMethod: "downloadCheckListReport", input: { jobId } };
            const res = await client().get(
                `${store.state.apiRouteRoot}CheckListReport/Download/${jobId}`,
                { responseType: "blob", meta }
            );
            const fname = filename || filenameFromDisposition(res, "checklist_report.pdf");
            return { blob: res.data, filename: fname };
        },

        // POST /api2/ServiceProvider/CheckListReport/Attach/{jobId}
        // Generates the current checklistreport, saves it as a Job Document.
        async attachCheckListReport(jobId) {
            const meta = { clientApiMethod: "attachCheckListReport", input: { jobId } };
            const res = await client().post(`${store.state.apiRouteRoot}CheckListReport/Attach/${jobId}`, null, { meta });
            return res?.data;
        },

    },
};